/* User administration - ADM-02, ADM-03.
 *
 * Pending accounts come first: they are the only rows that need a decision,
 * and an administrator opening this screen should see them without hunting.
 *
 * Approving requires naming the plants, because a role without a plant can
 * do nothing (ADM-02). The form therefore refuses to submit with none
 * selected rather than creating an account that signs in and sees an empty
 * application.
 */

import { useCallback, useEffect, useState } from 'react'
import { authedFetch, type Me } from '../api/auth'

interface AdminUser {
  id: number
  email: string
  full_name: string
  status: string
  roles: string[]
  plant_ids: number[]
  must_change_password: boolean
  approved_by: string | null
  last_login_at: string | null
  locked: boolean
}

interface RoleInfo {
  id: string
  description: string
  permissions: string[]
}

interface PlantInfo {
  id: number
  code: string
  name: string
}

export function Users({ me, embedded }: { me: Me; embedded?: boolean }) {
  const [users, setUsers] = useState<AdminUser[]>([])
  const [roles, setRoles] = useState<RoleInfo[]>([])
  const [plants, setPlants] = useState<PlantInfo[]>([])
  const [pending, setPending] = useState(0)
  const [admins, setAdmins] = useState(0)
  const [error, setError] = useState<string | null>(null)
  const [notice, setNotice] = useState<string | null>(null)
  const [editing, setEditing] = useState<AdminUser | null>(null)
  const [draftRoles, setDraftRoles] = useState<string[]>([])
  const [draftPlants, setDraftPlants] = useState<number[]>([])
  const [adding, setAdding] = useState(false)

  const load = useCallback(async () => {
    const [u, r, p] = await Promise.all([
      authedFetch('/api/admin/users').then((x) => x.json()),
      authedFetch('/api/admin/roles').then((x) => x.json()),
      authedFetch('/api/admin/plants').then((x) => x.json()),
    ])
    setUsers(u.users ?? [])
    setPending(u.pending ?? 0)
    setAdmins(u.active_administrators ?? 0)
    setRoles(r.roles ?? [])
    setPlants(p.plants ?? [])
  }, [])

  useEffect(() => {
    void load()
  }, [load])

  const act = async (path: string, init?: RequestInit) => {
    setError(null)
    setNotice(null)
    const response = await authedFetch(path, init)
    const body = await response.json()
    if (!response.ok) {
      setError(body.detail ?? 'the action was refused')
      return null
    }
    await load()
    if (body.temporary_password) {
      setNotice(
        `One-time password for ${body.email}: ${body.temporary_password} — ` +
          'it is shown once and must be replaced at first sign-in.',
      )
    }
    return body
  }

  const openEditor = (user: AdminUser) => {
    setEditing(user)
    setDraftRoles(user.roles.length ? user.roles : ['viewer'])
    setDraftPlants(user.plant_ids)
    setError(null)
  }

  return (
    <div style={{ display: 'grid', gap: 12 }}>
      <div className="card" style={{ padding: '14px 16px', display: 'flex', gap: 14, alignItems: 'center', flexWrap: 'wrap' }}>
        <div>
          {embedded ? <h2>Users and roles</h2> : <h1>Users and roles</h1>}
          <div className="small muted">
            {users.length} accounts · {admins} active administrator{admins === 1 ? '' : 's'}
            {admins === 1 && ' — the last one cannot be removed or demoted'}
          </div>
        </div>
        <div style={{ marginLeft: 'auto', display: 'flex', gap: 10, alignItems: 'center' }}>
          {pending > 0 && (
            <span className="chip warning">
              <span aria-hidden="true">▲</span>
              {pending} awaiting a decision
            </span>
          )}
          <button className="primary" onClick={() => setAdding(true)}>
            + Add user
          </button>
        </div>
      </div>

      {notice && (
        <div className="card" style={{ padding: '10px 14px', borderLeft: '4px solid var(--status-good)' }}>
          <span className="mono small">{notice}</span>
        </div>
      )}
      {error && (
        <div className="card" style={{ padding: '10px 14px', borderLeft: '4px solid var(--status-critical)' }}>
          <strong>⚠ Refused.</strong> {error}
        </div>
      )}

      <div className="card" style={{ padding: 0, overflow: 'auto' }}>
        <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 13 }}>
          <thead>
            <tr>
              {['', 'Name', 'Email', 'Roles', 'Plants', 'Last sign-in', 'Actions'].map((h) => (
                <th
                  key={h}
                  style={{
                    textAlign: 'left',
                    padding: '9px 12px',
                    borderBottom: '1px solid var(--border-strong)',
                    background: 'var(--surface-3)',
                    fontSize: 11,
                    fontWeight: 600,
                    whiteSpace: 'nowrap',
                  }}
                >
                  {h}
                </th>
              ))}
            </tr>
          </thead>
          <tbody>
            {users.map((u) => (
              <tr key={u.id} style={{ background: u.status === 'pending' ? 'var(--status-warning-bg)' : undefined }}>
                <td style={cell}><StatusChipFor status={u.status} locked={u.locked} /></td>
                <td style={{ ...cell, fontWeight: 600 }}>
                  {u.full_name}
                  {u.email === me.email && <span className="small muted"> (you)</span>}
                </td>
                <td style={cell} className="mono small">{u.email}</td>
                <td style={cell} className="small">{u.roles.join(', ') || '—'}</td>
                <td style={cell} className="mono small">
                  {u.plant_ids.map((id) => plants.find((p) => p.id === id)?.code ?? id).join(', ') || '—'}
                </td>
                <td style={cell} className="mono small muted">
                  {u.last_login_at ? u.last_login_at.replace('T', ' ').slice(0, 16) : 'never'}
                </td>
                <td style={{ ...cell, whiteSpace: 'nowrap' }}>
                  <div style={{ display: 'flex', gap: 5 }}>
                    {u.status === 'pending' ? (
                      <>
                        <button className="small primary" onClick={() => openEditor(u)}>Approve…</button>
                        <button
                          className="small"
                          onClick={() => void act(`/api/admin/users/${u.id}/reject`, {
                            method: 'POST',
                            body: JSON.stringify({ reason: 'rejected from the console' }),
                          })}
                        >
                          Reject
                        </button>
                      </>
                    ) : (
                      <>
                        <button className="small" onClick={() => openEditor(u)}>Access…</button>
                        <button
                          className="small"
                          onClick={() => void act(`/api/admin/users/${u.id}/reset-password`, { method: 'POST' })}
                        >
                          Reset password
                        </button>
                        {u.status === 'active' ? (
                          <button
                            className="small"
                            onClick={() => void act(`/api/admin/users/${u.id}/deactivate`, { method: 'POST' })}
                          >
                            Deactivate
                          </button>
                        ) : (
                          <button
                            className="small"
                            onClick={() => void act(`/api/admin/users/${u.id}/reactivate`, { method: 'POST' })}
                          >
                            Reactivate
                          </button>
                        )}
                      </>
                    )}
                  </div>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      <div className="card small" style={{ padding: 16 }}>
        <h3 style={{ marginBottom: 8 }}>What each role grants</h3>
        <div style={{ display: 'grid', gap: 6 }}>
          {roles.map((r) => (
            <div key={r.id} style={{ display: 'flex', gap: 10, alignItems: 'baseline' }}>
              <strong style={{ minWidth: 140 }}>{r.id.replace('_', ' ')}</strong>
              <span className="muted">{r.description}</span>
              <span className="chip neutral mono" style={{ marginLeft: 'auto' }}>
                {r.permissions.length} permissions
              </span>
            </div>
          ))}
        </div>
        <p className="muted" style={{ marginTop: 12, marginBottom: 0 }}>
          Roles are a set, not a level: a key user maintains master data but does not publish a plan, and a
          cluster viewer reads across plants but edits nothing. A user may hold several.
        </p>
      </div>

      {adding && (
        <NewUserDialog
          roles={roles}
          plants={plants}
          onClose={() => setAdding(false)}
          onCreate={async (body) => {
            const result = await act('/api/admin/users', { method: 'POST', body: JSON.stringify(body) })
            if (result) setAdding(false)
          }}
        />
      )}

      {editing && (
        <AccessDialog
          user={editing}
          roles={roles}
          plants={plants}
          draftRoles={draftRoles}
          draftPlants={draftPlants}
          setDraftRoles={setDraftRoles}
          setDraftPlants={setDraftPlants}
          onClose={() => setEditing(null)}
          onSave={async () => {
            const isPending = editing.status === 'pending'
            const body = JSON.stringify({ roles: draftRoles, plant_ids: draftPlants })
            const result = await act(
              isPending ? `/api/admin/users/${editing.id}/approve` : `/api/admin/users/${editing.id}/access`,
              { method: isPending ? 'POST' : 'PATCH', body },
            )
            if (result) setEditing(null)
          }}
        />
      )}
    </div>
  )
}

const cell: React.CSSProperties = { padding: '8px 12px', borderBottom: '1px solid var(--border)' }

function StatusChipFor({ status, locked }: { status: string; locked: boolean }) {
  if (locked) return <span className="chip critical"><span aria-hidden="true">▼</span>locked</span>
  const tone = status === 'active' ? 'good' : status === 'pending' ? 'warning' : 'neutral'
  const glyph = status === 'active' ? '●' : status === 'pending' ? '▲' : '·'
  return (
    <span className={`chip ${tone}`}>
      <span aria-hidden="true">{glyph}</span>
      {status}
    </span>
  )
}

function AccessDialog({
  user,
  roles,
  plants,
  draftRoles,
  draftPlants,
  setDraftRoles,
  setDraftPlants,
  onClose,
  onSave,
}: {
  user: AdminUser
  roles: RoleInfo[]
  plants: PlantInfo[]
  draftRoles: string[]
  draftPlants: number[]
  setDraftRoles: (v: string[]) => void
  setDraftPlants: (v: number[]) => void
  onClose: () => void
  onSave: () => void
}) {
  const toggle = <T,>(list: T[], value: T, set: (v: T[]) => void) =>
    set(list.includes(value) ? list.filter((v) => v !== value) : [...list, value])

  return (
    <div
      role="dialog"
      aria-label="Roles and plants"
      style={{
        position: 'fixed',
        inset: 0,
        background: 'rgba(11,11,11,.45)',
        display: 'grid',
        placeItems: 'center',
        padding: 16,
        zIndex: 60,
      }}
      onClick={onClose}
    >
      <div className="card" style={{ width: 'min(560px, 100%)', padding: 20 }} onClick={(e) => e.stopPropagation()}>
        <h2>{user.status === 'pending' ? 'Approve' : 'Change access'} — {user.full_name}</h2>
        <p className="small muted" style={{ margin: '4px 0 16px' }}>
          A role says what they may do; a plant says where. Both are required (ADM-02).
        </p>

        <h3 className="small" style={{ marginBottom: 6 }}>Roles</h3>
        <div style={{ display: 'grid', gap: 4, marginBottom: 16 }}>
          {roles.map((r) => (
            <label key={r.id} className="small" style={{ display: 'flex', gap: 8, alignItems: 'baseline' }}>
              <input
                type="checkbox"
                checked={draftRoles.includes(r.id)}
                onChange={() => toggle(draftRoles, r.id, setDraftRoles)}
              />
              <strong style={{ minWidth: 130 }}>{r.id.replace('_', ' ')}</strong>
              <span className="muted">{r.description}</span>
            </label>
          ))}
        </div>

        <h3 className="small" style={{ marginBottom: 6 }}>Plants</h3>
        <div style={{ display: 'flex', gap: 10, flexWrap: 'wrap', marginBottom: 18 }}>
          {plants.map((p) => (
            <label key={p.id} className="small" style={{ display: 'flex', gap: 6, alignItems: 'center' }}>
              <input
                type="checkbox"
                checked={draftPlants.includes(p.id)}
                onChange={() => toggle(draftPlants, p.id, setDraftPlants)}
              />
              <span className="mono">{p.code}</span>
            </label>
          ))}
          {plants.length === 0 && <span className="small muted">no plants yet</span>}
        </div>

        <div style={{ display: 'flex', gap: 10 }}>
          <button className="primary" onClick={onSave} disabled={!draftRoles.length || !draftPlants.length}>
            {user.status === 'pending' ? 'Approve account' : 'Save access'}
          </button>
          {(!draftRoles.length || !draftPlants.length) && (
            <span className="small muted" style={{ alignSelf: 'center' }}>
              at least one role and one plant
            </span>
          )}
          <button onClick={onClose} style={{ marginLeft: 'auto' }}>Cancel</button>
        </div>
      </div>
    </div>
  )
}

/* ADM-02: an administrator creates an account directly, with its roles and
 * plants. The password is generated by the server and shown once; the holder
 * replaces it at first sign-in. */
function NewUserDialog({
  roles,
  plants,
  onClose,
  onCreate,
}: {
  roles: RoleInfo[]
  plants: PlantInfo[]
  onClose: () => void
  onCreate: (body: { email: string; full_name: string; roles: string[]; plant_ids: number[] }) => void
}) {
  const [email, setEmail] = useState('')
  const [fullName, setFullName] = useState('')
  const [picked, setPicked] = useState<string[]>(['planner'])
  const [plantIds, setPlantIds] = useState<number[]>(plants.length === 1 ? [plants[0].id] : [])
  const toggle = <T,>(list: T[], value: T, set: (v: T[]) => void) =>
    set(list.includes(value) ? list.filter((v) => v !== value) : [...list, value])
  const ready = email.includes('@') && fullName.trim() && picked.length && plantIds.length

  return (
    <div className="modal-backdrop" role="dialog" aria-label="Add user" onClick={onClose}>
      <div className="card modal" style={{ width: 'min(560px, 100%)' }} onClick={(e) => e.stopPropagation()}>
        <div className="modal-head">
          <div>
            <h2>Add user</h2>
            <p className="small muted" style={{ margin: '4px 0 0' }}>
              A one-time password is generated and shown once; it must be changed at first sign-in.
            </p>
          </div>
          <button onClick={onClose}>Close</button>
        </div>

        <div className="param-grid" style={{ marginBottom: 14 }}>
          <label className="param-field">
            <span className="param-label">Full name</span>
            <input value={fullName} onChange={(e) => setFullName(e.target.value)} />
          </label>
          <label className="param-field">
            <span className="param-label">Email</span>
            <input type="email" value={email} onChange={(e) => setEmail(e.target.value)} />
          </label>
        </div>

        <h3 className="small" style={{ marginBottom: 6 }}>Roles</h3>
        <div style={{ display: 'grid', gap: 4, marginBottom: 14 }}>
          {roles.map((r) => (
            <label key={r.id} className="small" style={{ display: 'flex', gap: 8, alignItems: 'baseline' }}>
              <input type="checkbox" checked={picked.includes(r.id)} onChange={() => toggle(picked, r.id, setPicked)} />
              <strong style={{ minWidth: 130 }}>{r.id.replace('_', ' ')}</strong>
              <span className="muted">{r.description}</span>
            </label>
          ))}
        </div>

        <h3 className="small" style={{ marginBottom: 6 }}>Plants</h3>
        <div style={{ display: 'flex', gap: 10, flexWrap: 'wrap', marginBottom: 18 }}>
          {plants.map((p) => (
            <label key={p.id} className="small" style={{ display: 'flex', gap: 6, alignItems: 'center' }}>
              <input type="checkbox" checked={plantIds.includes(p.id)} onChange={() => toggle(plantIds, p.id, setPlantIds)} />
              <span className="mono">{p.code}</span>
            </label>
          ))}
        </div>

        <button
          className="primary"
          disabled={!ready}
          onClick={() => onCreate({ email: email.trim(), full_name: fullName.trim(), roles: picked, plant_ids: plantIds })}
        >
          Create account
        </button>
      </div>
    </div>
  )
}
